import { api } from '../../core/network/api';
import type { StratumId } from '../config/contract-defs';

/** design/gdd/contracts.md REST surface — plain wrappers, same convention as economy-api.ts. */

/** Mirrors backend/src/game/contracts/config/stratum-info.ts — derived server-side, never authored here. */
export interface StratumInfoRow {
  id: StratumId;
  name: string;
  /** 1-3; doubles as the difficulty rating, because depth IS the difficulty. */
  depth: number;
  description: string;
  threats: string[];
  preview: string;
}

/** What a contract changes about the dungeon. Never about the delver — see backend contract-defs.ts. */
export interface RunModifierRow {
  rareChanceBonus: number;
  bonewrightMultiplier: number;
  swarmSizeBonus: number;
}

export interface ContractDefRow {
  id: string;
  targetStratum: StratumId;
  name: string;
  blurb: string;
  priceWax: number;
  uses: number;
  modifier: RunModifierRow;
}

/**
 * One floor in the picker, and every card that buys into it.
 *
 * `contracts` replaced a single `contractDef` on 2026-09-04. `freeEntry` is
 * carried separately because the Cellars is both free *and* sells a card, so
 * "no contract" stopped meaning "free".
 */
export interface MapOptionRow {
  stratum: StratumInfoRow;
  contracts: ContractDefRow[];
  freeEntry: boolean;
}

export interface ContractRow {
  id: string;
  contractDefId: string;
  targetStratum: StratumId;
  priceWax: number;
  usesTotal: number;
  usesRemaining: number;
  status: 'AVAILABLE' | 'CONSUMED';
  purchasedAt: string;
}

export interface MyContracts {
  contracts: ContractRow[];
  /** Null means the next run starts in the Cellars. */
  selectedContractId: string | null;
}

export const getContractCatalog = (): Promise<MapOptionRow[]> => api.get('/contracts/catalog');

export const getMyContracts = (): Promise<MyContracts> => api.get('/contracts/mine');

export const purchaseContract = (contractDefId: string): Promise<{ contract: ContractRow; softBalance: number }> =>
  api.post('/contracts/purchase', { contractDefId });

/** `null` selects the free Cellars default — deselecting is as expressible as selecting. */
export const selectContract = (contractId: string | null): Promise<{ selectedContractId: string | null }> =>
  api.post('/contracts/select', { contractId });
